import React, {
  useContext, useEffect, useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router';

import CustomBtn from '../../../generic/CustomBtn/CustomBtn';
import './ProjectCardAction.scss';

import { contractMethods, getContract, getTxOutcome } from '../../../utils/near-utils';
import { GAS } from '../../../state/near';
import { appStore } from '../../../state/app';
import getConfig from '../../../config';
import { queueToastifyMessage } from '../../../components/Toastify/Toastify';
import {
  REGISTER_STATUS, ACTIVE_STATUS,
  CLOSED_STATUS, ASSIGN_PROGRESS,
  UPLOAD_PROGRESS, SUBMIT_PROGRESS,
  FINISHED_PROGRESS, SETUP_PROGRESS,
  NO_PROGRESS,
} from '../../../constans/constans';

const { deposit } = getConfig();

const ProjectCardAction = ({ isLoading, projectId, status, isStagesFinished, currentStage, stageProgress, isCarbon = false, dataOfProject }) => {
  const { state } = useContext(appStore);
  const { account } = state;
  const history = useHistory();
  const translate = useIntl();
  const [isMZPending, setIsMZPending] = useState(false);

  useEffect(() => {
    try {
      setIsMZPending(!!localStorage.getItem(`ofp_mz_pending_${projectId}`));
    } catch (e) {
      setIsMZPending(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (!isLoading && stageProgress !== SETUP_PROGRESS && stageProgress !== NO_PROGRESS) {
      try {
        localStorage.removeItem(`ofp_mz_pending_${projectId}`);
      } catch (e) {
        // ignore localStorage errors
      }
      setIsMZPending(false);
    }
  }, [stageProgress, projectId, isLoading]);

  const closeProject = async () => {
    try {
      const contract = getContract(account, contractMethods, 0);
      const txResult = await contract.update_project_status({
        project_id: projectId,
        project_status: CLOSED_STATUS,
      }, GAS, deposit);
      const { isSuccess, methodName } = getTxOutcome(txResult);
      if (!isSuccess || methodName !== 'update_project_status') {
        throw new Error('update_project_status tx was not successful');
      }
      queueToastifyMessage(translate.formatMessage({ id: 'smart-contract-notify' }));
      window.location.reload();
    } catch (e) {
      console.log(e);
    }
  };
  if (isLoading) {
    return (
      <div className="card-action">
        <span className="card-action__loading">
          {translate.formatMessage({ id: 'projectCard.loading' })}
          ...
        </span>
      </div>
    );
  }

  // If project status is paused, failed, terminated or closed
  if (status !== ACTIVE_STATUS && status !== REGISTER_STATUS) return null;
  const showUploadLabel = dataOfProject?.leakage?.carbonApprove ?? true;
  let label;

  switch (true) {
    case showUploadLabel && dataOfProject?.carbonApplication?.completed:
      label = 'projectCard.carbonView';
      break;
    case showUploadLabel:
      label = 'projectCard.carbonApplication';
      break;
    default:
      label = 'projectCard.carbonUpdate';
      break;
  }
  if (status === REGISTER_STATUS) {
    if (isCarbon && !dataOfProject?.leakage?.carbonApprove) {
      return (
        <CustomBtn
          label={translate.formatMessage({ id: label })}
          handleClick={() => history.push(`/project/${projectId}/carbon/application`)}
          customClass="card-action card-action__start"
        />
      );
    }
    return (
      <CustomBtn
        label={translate.formatMessage({ id: 'projectCard.start' })}
        handleClick={() => history.push(`/data-upload/${projectId}`)}
        customClass="card-action card-action__start"
      />
    );
  }

  // If last project's stage is finished
  if (isStagesFinished) {
    return (
      <CustomBtn
        label={translate.formatMessage({ id: 'projectCard.end' })}
        handleClick={() => closeProject()}
        customClass="card-action card-action__end"
      />
    );
  }

  // In case if project's stages are missed
  if (status === ACTIVE_STATUS && currentStage === -1) return null;

  // If report was submitted on active stage
  if (stageProgress === FINISHED_PROGRESS) {
    return (
      <div className="card-action cursor-default">
        <span className="card-action__stageComplete">
          {translate.formatMessage({ id: 'projectCard.stageComplete' })}
          <i className="icon-success" />
        </span>
      </div>
    );
  }

  // If report is waiting to be submitted or sent by field agent
  if (stageProgress === UPLOAD_PROGRESS || stageProgress === SUBMIT_PROGRESS) {
    return (
      <CustomBtn
        label={translate.formatMessage({ id: 'projectCard.prepare' })}
        handleClick={() => history.push(`/project/${projectId}/monitoring`)}
        customClass="card-action card-action__prepare"
      />
    );
  }

  // If need to assign field agent
  if (stageProgress === ASSIGN_PROGRESS) {
    return (
      <CustomBtn
        label={translate.formatMessage({ id: 'projectCard.assignFA' })}
        handleClick={() => history.push(`/project/${projectId}/field-agents`)}
        customClass="card-action card-action__assign"
      />
    );
  }

  // If need to set up monitoring zone and sample plots on active stage
  if (isMZPending && stageProgress === SETUP_PROGRESS) return null;
  return (
    <CustomBtn
      label={translate.formatMessage({ id: 'projectCard.setupMZ' })}
      handleClick={() => history.push(`/data-upload/${projectId}`)}
      customClass="card-action card-action__setup"
    />
  );
};

export default ProjectCardAction;
